import * as React from 'react';
import { cn } from '@/shared/lib/cn';
import { useMaskedInput } from '@/shared/lib/useMaskedInput';
import { Button } from '@/shared/ui/Button';
import { Calendar } from '@/shared/ui/Calendar/Calendar';
import {
  addDays,
  addMonths,
  clampIso,
  completeDate,
  formatRu,
  isoToday,
  maskDateEdit,
  parseRu,
} from '@/shared/ui/Calendar/calendar-date';
import { Input, type InputProps } from '@/shared/ui/Input/Input';
import { PickerShell } from './PickerShell';
import styles from './picker.module.css';

/**
 * Поле даты: маска `ДД.ММ.ГГГГ` плюс календарь во всплывашке.
 *
 * Заменяет нативный `<input type="date">`, который рисует браузер: тот не
 * берёт токены темы (в тёмной теме это белый прямоугольник), в каждом браузере
 * выглядит по-своему, не умеет диапазон и не знает ни про `min` с подсказкой,
 * ни про «сегодня» одной кнопкой.
 *
 * Клавиатура важнее календаря: дату быстрее набрать, чем найти в сетке.
 * Поэтому поле — обычный ввод с маской, стрелки двигают значение на день
 * (с Shift — на неделю), `PageUp`/`PageDown` — на месяц, а календарь
 * открывается кнопкой или `Alt` + стрелка вниз.
 *
 * Значение — `YYYY-MM-DD` или пустая строка. Никаких `Date`: разбор строки
 * конструктором даёт полночь UTC и сдвигает день (см. `calendar-date.ts`).
 *
 * @use ввод даты: поле с маской ДД.ММ.ГГГГ и календарём. Вместо `<input type="date">`.
 */
export interface DatePickerProps
  extends Omit<InputProps, 'value' | 'onChange' | 'type' | 'onBlur'> {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  isDisabled?: (iso: string) => boolean;
  clearable?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
}

export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  {
    value,
    onChange,
    min,
    max,
    isDisabled,
    clearable = true,
    className,
    disabled,
    onKeyDown,
    onBlur,
    ...rest
  },
  ref,
) {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState(() => formatRu(value));

  React.useEffect(() => {
    setText((current) => (parseRu(current) === value ? current : formatRu(value)));
  }, [value]);

  const emit = (next: string) => {
    const iso = parseRu(next);
    if (iso !== value) onChange(iso);
  };

  const masked = useMaskedInput({
    value: text,
    mask: maskDateEdit,
    onChange: (next) => {
      setText(next);
      emit(next);
    },
  });
  React.useImperativeHandle(ref, () => masked.ref.current as HTMLInputElement, [masked.ref]);

  const settle = (raw: string) => {
    const done = completeDate(raw);
    const iso = parseRu(done);
    const clamped = iso === '' ? '' : clampIso(iso, min, max);
    setText(clamped === '' ? done : formatRu(clamped));
    if (clamped !== value) onChange(clamped);
  };

  const step = (delta: number) => {
    const base = parseRu(completeDate(text)) || clampIso(isoToday(), min, max);
    const next = clampIso(delta === 0 ? base : addDays(base, delta), min, max);
    setText(formatRu(next));
    if (next !== value) onChange(next);
  };

  const stepMonths = (delta: number) => {
    const base = parseRu(completeDate(text)) || clampIso(isoToday(), min, max);
    const next = clampIso(addMonths(base, delta), min, max);
    setText(formatRu(next));
    if (next !== value) onChange(next);
  };

  const pick = (iso: string) => {
    setText(formatRu(iso));
    if (iso !== value) onChange(iso);
    setOpen(false);
  };

  return (
    <PickerShell
      open={open}
      onOpenChange={setOpen}
      disabled={disabled}
      filled={text !== ''}
      finalFocus={masked.ref}
      className={className}
      triggerLabel="Открыть календарь"
      onClear={clearable && value !== '' ? () => pick('') : undefined}
      clearLabel="Очистить дату"
      popup={
        <Calendar
          value={value}
          onSelect={pick}
          min={min}
          max={max}
          isDisabled={isDisabled}
          autoFocusDay
          aria-label="Выбор даты"
          footer={
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => pick(clampIso(isoToday(), min, max))}
              >
                Сегодня
              </Button>
            </>
          }
        />
      }
    >
      <Input
        ref={masked.ref}
        className={cn(styles.input)}
        value={text}
        disabled={disabled}
        inputMode="numeric"
        autoComplete="off"
        placeholder="дд.мм.гггг"
        maxLength={10}
        onChange={masked.onChange}
        onKeyDown={(event) => {
          if (event.altKey && event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
          } else if (event.key === 'Escape' && open) {
            event.preventDefault();
            setOpen(false);
          } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            step((event.key === 'ArrowUp' ? 1 : -1) * (event.shiftKey ? 7 : 1));
          } else if (event.key === 'PageUp' || event.key === 'PageDown') {
            event.preventDefault();
            stepMonths(event.key === 'PageUp' ? 1 : -1);
          }
          onKeyDown?.(event);
        }}
        onBlur={(event) => {
          settle(text);
          onBlur?.(event);
        }}
        {...rest}
      />
    </PickerShell>
  );
});
